/**
 * "Requests" gadget on the project Home: how many requests the services answered and how many failed, from the Traces
 * map's totals (`GET /traces/map`), with a bar per slot of the window (at most 48). It asks for 24 hours; Flow keeps the requests of the last hour
 * (vfs-service TRACES_MAX_AGE_MS), so the title says the window the answer really covers. Nothing is invented: tracing
 * off or no request yet is said as it is. The map is the one API health and Errors read too (gadgets/sources.js).
 */
import React from 'react';
import PropTypes from 'prop-types';
import { formatMs } from '../traces/waterfallLayout';
import { useTracesMap } from './gadgets/sources';
import { GadgetSkeleton } from './gadgets/GadgetCard';

/** "last hour", "last 24 hours", "last 15 minutes" for the window a map answer covers. */
export function windowLabel(map) {
  const minutes = Math.round(((map?.until || 0) - (map?.since || 0)) / 60000);
  if (!(minutes > 0)) { return 'recently'; }
  if (minutes >= 1380) { return 'last 24 hours'; }
  if (minutes >= 55 && minutes <= 65) { return 'last hour'; }
  return minutes >= 120 ? `last ${Math.round(minutes / 60)} hours` : `last ${minutes} minutes`;
}

/**
 * The per-minute counts grouped into at most `slots` bars over the window the map covers (24 hours of minutes would be
 * 1,440 hair-thin bars): `[{ t, total, failed }]`, a slot with no request has total 0.
 */
export function groupBars(map, slots = 48) {
  const minutes = Array.isArray(map?.perMinute) ? map.perMinute : [];
  if (minutes.length === 0) { return []; }
  const times = minutes.map((bucket) => bucket.t);
  const since = Number.isFinite(map?.since) && map.until > map.since ? map.since : Math.min(...times);
  const until = Number.isFinite(map?.until) && map.until > map.since ? map.until : Math.max(...times) + 60000;
  const count = Math.max(1, Math.min(slots, Math.ceil((until - since) / 60000)));
  const width = (until - since) / count;
  const bars = Array.from({ length: count }, (_, index) => ({ t: since + index * width, total: 0, failed: 0 }));
  minutes.forEach((bucket) => {
    const slot = bars[Math.min(count - 1, Math.max(0, Math.floor((bucket.t - since) / width)))];
    slot.total += bucket.total || 0;
    slot.failed += bucket.failed || 0;
  });
  return bars;
}

export default function RequestsGadget({ projectId, onOpenTraces = null }) {
  const { data: map, error, loading } = useTracesMap(projectId);
  const totals = map?.totals;
  const bars = groupBars(map);
  const peak = Math.max(1, ...bars.map((bucket) => bucket.total || 0));
  let body;
  if (loading && !map) { body = <GadgetSkeleton label="Reading the requests…" />; }
  else if (error && !map) { body = <p className="ph-quiet">The requests could not be read just now.</p>; }
  else if (map?.enabled === false) { body = <p className="ph-quiet">Tracing is off, so requests are not counted.</p>; }
  else if (!totals?.requests) { body = <p className="ph-quiet">No request in the {windowLabel(map)}. Open the app and use it to see them here.</p>; }
  else {
    body = (
      <>
        <p className="rq-total" data-testid="requests-total">
          <strong>{totals.requests}</strong> {totals.requests === 1 ? 'request' : 'requests'}
          {' · '}
          <span className={totals.failed > 0 ? 'rq-bad' : ''}>{totals.failed} {totals.failed === 1 ? 'error' : 'errors'}</span>
          {totals.p95Ms > 0 && <span className="ph-muted"> · p95 {formatMs(totals.p95Ms)}</span>}
        </p>
        <div aria-hidden="true" className="rq-bars">
          {bars.map((bucket) => (
            <span className={bucket.failed > 0 ? 'is-bad' : ''} key={bucket.t} style={{ height: bucket.total > 0 ? `${Math.max(8, Math.round((bucket.total / peak) * 100))}%` : 0 }} />
          ))}
        </div>
      </>
    );
  }
  return (
    <div className="ph-card" data-testid="requests-gadget">
      <div className="ph-card-head">
        <h2 className="ph-card-title">Requests</h2>
        {map?.enabled !== false && totals?.requests > 0 && <span className="ph-muted ph-small">{windowLabel(map)}</span>}
        {onOpenTraces && <button className="ph-link ph-card-links" onClick={onOpenTraces} type="button">Traces</button>}
      </div>
      {body}
    </div>
  );
}

RequestsGadget.propTypes = { onOpenTraces: PropTypes.func, projectId: PropTypes.string };
